import { type Architecture, type Browser, type Device, type OS } from '~/types/Device';

const IOS_PATTERN = /(iPhone|iPad|iPod|iOS)/i;
const MACOS_PATTERN = /(Macintosh|MacIntel|MacPPC|Mac68K|Mac|MacOS)/i;
const WINDOWS_PATTERN = /(Win32|Win64|Windows|WinCE)/i;
const ANDROID_PATTERN = /Android/i;
const CHROME_OS_PATTERN = /(CrOS|ChromeOS|Chrome OS)/i;
const LINUX_PATTERN = /Linux/i;

const ARM_PATTERN = /(arm|arm64|aarch64)/i;
const X86_PATTERN = /(x86|x64|amd64|wow64|ia32|i386|i686|win64)/i;

const CHROME_PATTERN = /(chrome|chromium|crios)/i;
const FIREFOX_PATTERN = /(firefox|fxios)/i;
const SAFARI_PATTERN = /safari/i;
const OPERA_PATTERN = /(opr\/|opera)/i;
const EDGE_PATTERN = /edg/i;
const IE_PATTERN = /(msie|trident)/i;

const parseBrowser = (rawBrowser: string | undefined): Browser => {
  if (!rawBrowser) return 'unknown';
  if (EDGE_PATTERN.test(rawBrowser)) return 'edge';
  if (OPERA_PATTERN.test(rawBrowser)) return 'opera';
  if (CHROME_PATTERN.test(rawBrowser)) return 'chrome';
  if (FIREFOX_PATTERN.test(rawBrowser)) return 'firefox';
  if (SAFARI_PATTERN.test(rawBrowser)) return 'safari';
  if (IE_PATTERN.test(rawBrowser)) return 'ie';
  return 'unknown';
};

const parseOS = (rawOS: string | undefined): OS => {
  const hasTouchSupport: boolean =
    (typeof window !== 'undefined' && window.navigator.maxTouchPoints > 0) ||
    (typeof document !== 'undefined' && 'ontouchend' in document);

  if (!rawOS) return 'unknown';
  if (IOS_PATTERN.test(rawOS)) return 'ios';
  if (MACOS_PATTERN.test(rawOS)) return hasTouchSupport ? 'ios' : 'macos';
  if (WINDOWS_PATTERN.test(rawOS)) return 'windows';
  if (ANDROID_PATTERN.test(rawOS)) return 'android';
  if (CHROME_OS_PATTERN.test(rawOS)) return 'chromeos';
  if (LINUX_PATTERN.test(rawOS)) return 'linux';
  return 'unknown';
};

const parseArchitecture = (rawArchitecture: string | undefined): Architecture => {
  if (!rawArchitecture) return 'unknown';
  if (ARM_PATTERN.test(rawArchitecture)) return 'arm';
  if (X86_PATTERN.test(rawArchitecture)) return 'x86';
  return 'unknown';
};

const getDevice = async (): Promise<Device> => {
  const { userAgent, userAgentData } = navigator;

  const fallback: Device = {
    architecture: parseArchitecture(userAgent),
    browser: parseBrowser(userAgent),
    os: parseOS(userAgent),
  };

  if (!userAgentData?.getHighEntropyValues) return fallback;

  const os: OS = parseOS(userAgentData?.platform);
  const browser: Browser = parseBrowser(userAgentData?.brands?.[0]?.brand);
  let architecture: Architecture = 'unknown';

  try {
    const { architecture: rawArchitecture } = await userAgentData.getHighEntropyValues([
      'architecture',
    ]);
    architecture = parseArchitecture(rawArchitecture);
  } catch {
    // Fallbacks to navigator.userAgent
  }

  return {
    architecture: architecture !== 'unknown' ? architecture : fallback.architecture,
    browser: browser !== 'unknown' ? browser : fallback.browser,
    os: os !== 'unknown' ? os : fallback.os,
  };
};

export { getDevice };
